/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Controller, { inject as controller } from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import ReportTemplateType from '@linc-technologies/linced-common/enums/reports/report-template-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';
import { loadDeletedReportTemplates } from 'linced-app/utils/deleted-report-templates';

const CREATE_FROM_SCRATCH = { id: 'scratch', label: 'Create from scratch' };

export default class TemplatesController extends Controller {
  @controller('customise/report-builder')
  reportBuilder;

  // Services
  @service
  fetch;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @tracked
  showCreateReportDialog = false;
  @tracked
  selectedCreateOption = null;
  @tracked
  globalTemplates = null;
  @tracked
  isLoadingGlobalTemplates = false;
  @tracked
  isCreating = false;
  @tracked
  deleteDialogOpen = false;
  @tracked
  templateToDelete = null;
  @tracked
  isDeleting = false;

  // Id of the deleted template currently being restored (drives the per-row
  // button's loading state); null when none is in flight.
  @tracked
  restoringId = null;

  // Soft-deleted templates, loaded by the route (LINC-ED only) and kept
  // separate from the findAll collection so they can't leak into the
  // active/inactive lists. Already filtered + sorted route-side.
  @alias('model.deletedTemplates')
  deletedReportTemplates;

  // The deleted list is a LINC-ED-internal recovery view; the endpoint itself
  // is restricted to LINC-ED staff, and we gate the section to match.
  @computed
  get isLincedStaff() {
    return this.permissions.meet(Scopes.LINCED_MANAGER);
  }

  // The school's current report templates, by name. The store also holds
  // templates loaded by id elsewhere (e.g. listing student reports loads each
  // report's template), which can include soft-deleted ones. Those are never
  // shown here; LINC-ED staff see them in the separate Deleted list.
  @computed('model.templates.@each.{type,next,isGlobal,deleteTime,name}')
  get reportTemplates() {
    return (this.model.templates || [])
      .filter(
        (t) =>
          t.get('type') === ReportTemplateType.REPORT &&
          !t.get('next') &&
          !t.get('isGlobal') &&
          !t.get('deleteTime'),
      )
      .sort(byKeys('name'));
  }

  @computed('reportTemplates.@each.isActive')
  get activeReportTemplates() {
    return this.reportTemplates.filter((t) => t.isActive === true);
  }

  // Anything not active, including templates with isActive unset
  @computed('reportTemplates.@each.isActive')
  get inactiveReportTemplates() {
    return this.reportTemplates.filter((t) => !t.isActive);
  }

  // "Create from scratch" sits on its own at the top; everything copyable is
  // grouped below it. The active/inactive lists are already filtered to this
  // school's current, non-deleted report templates, so deleted templates
  // can't be copied.
  @computed(
    'globalTemplates.[]',
    'activeReportTemplates.[]',
    'inactiveReportTemplates.[]',
  )
  get createOptions() {
    let toOption = (t) => ({ id: t.id, label: t.name, template: t });
    let addGroup = (options, groupName, templates) => {
      let groupOptions = (templates || []).map(toOption);
      if (groupOptions.length) {
        options.push({ groupName, options: groupOptions });
      }
    };

    let options = [CREATE_FROM_SCRATCH];
    addGroup(options, 'Templates', this.globalTemplates);
    addGroup(options, 'Active reports', this.activeReportTemplates);
    addGroup(options, 'Inactive reports', this.inactiveReportTemplates);
    return options;
  }

  _loadGlobalTemplates() {
    if (this.isLoadingGlobalTemplates) return;
    this.isLoadingGlobalTemplates = true;
    let jurisdiction = this.get('session.school.jurisdiction');
    this.store
      .query('reports/report-template', {
        isGlobal: true,
        jurisdiction: jurisdiction,
      })
      .then((results) => {
        this.globalTemplates = results;
      })
      .catch(() => {
        // Swallow: global templates are optional in the create dialog.
      })
      .finally(() => {
        this.isLoadingGlobalTemplates = false;
      });
  }

  @action
  openTemplate(template) {
    this.router.transitionTo(
      'customise.report-builder-edit',
      template.get('id'),
    );
  }

  @action
  deleteTemplate(template) {
    this.templateToDelete = template;
    this.deleteDialogOpen = true;
  }

  @action
  cancelDelete() {
    // Reset isDeleting too: dismissing via Escape/click-outside while a
    // delete is in flight would otherwise leave the next dialog stuck in the
    // "Deleting..." state.
    this.deleteDialogOpen = false;
    this.templateToDelete = null;
    this.isDeleting = false;
  }

  @action
  async confirmDeleteTemplate() {
    let template = this.templateToDelete;
    if (!template) return;
    this.isDeleting = true;
    try {
      // Soft delete server-side: the template drops out of list queries but
      // remains resolvable by id for existing student reports.
      await template.destroyRecord();
      this.deleteDialogOpen = false;
      this.templateToDelete = null;
      // Refresh the deleted list so the just-deleted template appears in the
      // "Deleted" section (LINC-ED only) without a full reload.
      this.deletedReportTemplates = await loadDeletedReportTemplates(this);
    } catch (e) {
      // Undo the local deleted state so the card stays put on failure.
      template.rollbackAttributes();
      this.toaster.error('Error deleting report template!');
    } finally {
      this.isDeleting = false;
    }
  }

  // Restore (undelete) a soft-deleted template via the LINC-ED-only restore
  // endpoint. On success the restored template is pushed into the store — so
  // it reappears in the active/inactive lists (findAll's live array picks it
  // up) — and removed from the deleted list.
  @action
  async restoreTemplate(template) {
    if (this.restoringId) return;
    this.restoringId = template.id;
    try {
      let adapter = this.store.adapterFor('reports/report-template');
      let host = adapter.host || '';
      let url = `${host}/${adapter.namespace}/report-templates/${template.id}/restore`;
      let payload = await this.fetch.post(url, {
        data: JSON.stringify({}),
        headers: { 'Content-Type': 'application/json' },
      });
      if (payload) {
        this.store.pushPayload('reports/report-template', payload);
      }
      // Drop it from the deleted list (reassign so the alias/each re-renders).
      this.deletedReportTemplates = (this.deletedReportTemplates || []).filter(
        (t) => t.id !== template.id,
      );
      this.toaster.success('Report template restored.');
    } catch (e) {
      this.toaster.error('Error restoring report template!');
    } finally {
      this.restoringId = null;
    }
  }

  @action
  openCreateReportDialog() {
    this.selectedCreateOption = CREATE_FROM_SCRATCH;
    this.showCreateReportDialog = true;
    this._loadGlobalTemplates();
  }

  @action
  closeCreateReportDialog() {
    this.showCreateReportDialog = false;
    this.selectedCreateOption = null;
  }

  @action
  selectCreateOption(option) {
    this.selectedCreateOption = option;
  }

  @action
  async confirmCreateReport() {
    let option = this.selectedCreateOption;

    if (!option || option.id === 'scratch') {
      this.showCreateReportDialog = false;
      this.selectedCreateOption = null;
      this.router.transitionTo('customise.report-builder');
      return;
    }

    // Copy the chosen template — either a Hero global one or one of this
    // school's own reports. Same endpoint either way.
    this.isCreating = true;
    try {
      let adapter = this.store.adapterFor('reports/report-template');
      let host = adapter.host || '';
      let url = `${host}/${adapter.namespace}/report-templates/${option.id}/copy`;
      let payload = await this.fetch.post(url, {
        data: JSON.stringify({ schoolId: this.session.activeSchool }),
        headers: { 'Content-Type': 'application/json' },
      });
      this.store.pushPayload('reports/report-template', payload);
      let newTemplateId = payload.reportTemplate
        ? payload.reportTemplate.id
        : payload.id;

      // A copy always starts inactive, even when taken from an active
      // report, so it can't reach teachers before it has been reviewed. The
      // first save offers the activate prompt.
      let newTemplate = this.store.peekRecord(
        'reports/report-template',
        newTemplateId,
      );
      if (newTemplate) {
        newTemplate.isActive = false;
      }

      this.showCreateReportDialog = false;
      this.selectedCreateOption = null;
      // Skip the baseline snapshot so the copy reads as dirty (Save enabled).
      this.reportBuilder.set('freshCopy', true);
      this.router.transitionTo('customise.report-builder-edit', newTemplateId);
    } catch (e) {
      this.toaster.error('Error copying report template!');
    } finally {
      this.isCreating = false;
    }
  }
}
